import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import confetti from 'canvas-confetti'; // Importér canvas-confetti
import Ikon from './ny-kundesamhandling/Ikon';
import { Tilbake, Sidetopp, feltKlasse, knappPrimar, knappSekundar } from './ny-kundesamhandling/Skjema';
import { useErLeder, jobberI } from './employeeAccess';

// Hent dagens dato i formatet yyyy-mm-dd for input type="date"
const today = new Date().toISOString().split('T')[0];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function TodoList() {
    const [dailyTasks, setDailyTasks] = useState([]);
    const [customTasks, setCustomTasks] = useState([]);
    const [newCustomTask, setNewCustomTask] = useState('');
    const [completedTasks, setCompletedTasks] = useState([]);
    const [highlightedTaskId, setHighlightedTaskId] = useState(null); // Legg til denne for ramme rundt ny oppgave

    // Hent butikk-ID fra cookies som i OrderList
    const butikkid = parseInt(Cookies.get('butikkid'), 10) || null;

    const [customTaskDate, setCustomTaskDate] = useState(today);
    const [employee] = useState(Cookies.get('selectedEmployee') || '');

    const [editableTaskId, setEditableTaskId] = useState(null);
    const [editableTaskText, setEditableTaskText] = useState('');

    // Leder kan avslutte en oppgave for en ansatt som glemte det: velg ansatt og dato.
    const leder = useErLeder();
    const [ansatte, setAnsatte] = useState([]);
    const [avslutter, setAvslutter] = useState(null); // { id, type, employee, dato, feil, lagrer }

    useEffect(() => {
        if (!leder) return;
        (async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/employees`);
                if (!response.ok) return;
                const butikk = Cookies.get('selectedStore');
                const liste = (await response.json())
                    .filter((emp) => emp.navn && jobberI(emp, butikk))
                    .map((emp) => emp.navn)
                    .sort((a, b) => a.localeCompare(b, 'nb'));
                setAnsatte(liste);
            } catch (error) {
                console.error('Feil ved henting av ansatte:', error);
            }
        })();
    }, [leder]);

    // Hent daglige oppgaver
    const fetchDailyTasks = useCallback(async () => {
        try {
            const dailyTasksResponse = await fetch(`${API_BASE_URL}/dailytasks`);
            const dailyTasksData = await dailyTasksResponse.json();

            // Filtrer oppgaver relatert til butikkID
            const filteredDailyTasks = dailyTasksData.filter((task) => task.store === butikkid);

            const updatedDailyTasks = [];

            for (const task of filteredDailyTasks) {
                if (task.completed && task.dateCompleted) {
                    const completedDate = new Date(task.dateCompleted).toISOString().split('T')[0];

                    if (completedDate !== today) {
                        // Logg oppgaven som skal oppdateres

                        // Oppdater oppgaven i databasen hvis datoen ikke samsvarer med dagens dato
                        const updateResponse = await fetch(
                            `${API_BASE_URL}/dailytasks/${task._id}`,
                            {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    completed: false,
                                    completedBy: null,
                                    dateCompleted: null,
                                }),
                            }
                        );

                        if (updateResponse.ok) {
                            const updatedTask = await updateResponse.json();
                            console.log('Oppgave oppdatert til completed: false:', updatedTask);
                            updatedDailyTasks.push({
                                ...task,
                                completed: false,
                                completedBy: null,
                            });
                        } else {
                            console.error(
                                'Feil ved oppdatering av oppgave:',
                                updateResponse.status
                            );
                        }
                    } else {
                        // Logg oppgaven som har fullført dato som dagens dato

                        updatedDailyTasks.push(task); // Oppgaven er fortsatt fullført for i dag
                    }
                } else {
                    updatedDailyTasks.push(task); // Ikke fullført oppgave legges til som den er
                }
            }

            setDailyTasks(updatedDailyTasks);
        } catch (error) {
            console.error('Feil ved henting av daglige oppgaver:', error);
        }
    }, [butikkid]);

    // Hent egendefinerte oppgaver
    const fetchCustomTasks = useCallback(async () => {
        try {
            console.log('Starter henting av egendefinerte oppgaver...');

            const customTasksResponse = await fetch(`${API_BASE_URL}/customtasks`);
            const customTasksData = await customTasksResponse.json();

            console.log('Rådata fra API-et:', customTasksData);

            const filteredCustomTasks = customTasksData.filter((task) => task.store === butikkid);
            console.log('Filtrerte oppgaver for butikkID:', filteredCustomTasks);

            const updatedCustomTasks = [];

            for (const task of filteredCustomTasks) {
                console.log(
                    `Behandler oppgave: ${task.task}, completed: ${task.completed}, dateCompleted: ${task.dateCompleted}`
                );

                if (task.dateCompleted) {
                    // Sjekk om det finnes en dateCompleted
                    const completedDate = new Date(task.dateCompleted).toISOString().split('T')[0];
                    console.log(
                        `Oppgave: ${task.task}, utførtdato: ${completedDate}, dagens dato: ${today}`
                    );

                    // Sjekk om dateCompleted er ulik dagens dato
                    if (completedDate !== today) {
                        console.log(
                            `Oppgave "${task.task}" har en utførtdato (${completedDate}) som ikke samsvarer med dagens dato (${today}) og vil bli slettet.`
                        );

                        // Slett oppgaven fra customtasks
                        const deleteResponse = await fetch(
                            `${API_BASE_URL}/customtasks/${task._id}`,
                            {
                                method: 'DELETE',
                            }
                        );

                        if (deleteResponse.ok) {
                            console.log(`Oppgave "${task.task}" ble slettet.`);
                        } else {
                            console.error(
                                `Feil ved sletting av oppgave ${task.task}: ${deleteResponse.statusText}`
                            );
                        }
                    } else {
                        console.log(
                            `Oppgave "${task.task}" har riktig utførtdato og vil bli beholdt.`
                        );
                        // Behold oppgaver som har riktig utførtdato
                        updatedCustomTasks.push(task);
                    }
                } else {
                    console.log(`Oppgave "${task.task}" har ikke fullførtdato og vil bli beholdt.`);
                    // Behold oppgaver som ikke har dateCompleted
                    updatedCustomTasks.push(task);
                }
            }

            console.log('Oppdaterte egendefinerte oppgaver:', updatedCustomTasks);

            // Oppdaterer customTasks state med gyldige oppgaver
            setCustomTasks(updatedCustomTasks);
        } catch (error) {
            console.error('Feil ved henting av egendefinerte oppgaver:', error);
        }
    }, [butikkid]);

    // Hent fullførte oppgaver
    const fetchCompletedTasks = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/completedtasks`);
            const data = await response.json();

            const filteredTasks = data.filter((task) => task.store === butikkid);

            setCompletedTasks(filteredTasks.slice(-10).reverse()); // Hent de siste 10 oppgavene og sorter nyeste først
        } catch (error) {
            console.error('Feil ved henting av fullførte oppgaver:', error);
        }
    }, [butikkid]);

    useEffect(() => {
        fetchDailyTasks();
        fetchCustomTasks(); // Sørg for å hente og slette før siden lastes
        fetchCompletedTasks();
    }, [fetchDailyTasks, fetchCustomTasks, fetchCompletedTasks]);

    // Funksjon for å legge til en ny egendefinert oppgave
    const handleAddCustomTask = async () => {
        if (!newCustomTask || !customTaskDate || !butikkid) {
            alert('Oppgaven, dato og butikk-ID må fylles ut.');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/customtasks`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    task: newCustomTask,
                    dueDate: customTaskDate,
                    store: butikkid,
                }),
            });

            if (response.ok) {
                const addedTask = await response.json();
                setCustomTasks((prevTasks) => [...prevTasks, addedTask]);
                setNewCustomTask('');
                setCustomTaskDate(today);
            } else {
                console.error('Feil ved lagring av oppgave');
            }
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
        }
    };

    // Funksjon for å markere en daglig oppgave som fullført
    const handleCompleteDailyTask = async (taskId, taskDescription) => {
        const bodyData = {
            task: taskDescription,
            taskType: 'daily',
            dateCompleted: new Date().toISOString(),
            employee,
            store: butikkid,
        };

        try {
            // Legg til fullført oppgave i completedtasks-tabellen
            const completedResponse = await fetch(`${API_BASE_URL}/completedtasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData),
            });

            if (!completedResponse.ok) throw new Error('Feil ved oppdatering av completedtasks');

            window.dispatchEvent(new Event('gjoremalUtfort')); // «Siste gjøremål» i menylinjen

            const completedData = await completedResponse.json();
            const completedTaskId = completedData._id;

            // Oppdater dailytask som fullført i databasen
            const response = await fetch(`${API_BASE_URL}/dailytasks/${taskId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    completed: true,
                    completedBy: employee,
                    dateCompleted: new Date().toISOString(),
                }),
            });

            if (response.ok) {
                setDailyTasks((prevTasks) =>
                    prevTasks.map((task) =>
                        task._id === taskId
                            ? {
                                  ...task,
                                  completed: true,
                                  completedBy: employee,
                                  dateCompleted: new Date().toISOString().split('T')[0],
                              }
                            : task
                    )
                );
                await fetchCompletedTasks(); // Oppdater fullførte oppgaver
                setHighlightedTaskId(completedTaskId);
                setTimeout(() => setHighlightedTaskId(null), 5000);

                // Trigg konfetti-animasjon
                confetti({
                    particleCount: 150,
                    spread: 70,
                    origin: { y: 0.6 },
                });
            } else {
                console.error('Feil ved oppdatering av dailytasks', response.statusText);
            }
        } catch (error) {
            console.error('Feil ved håndtering av daglig oppgave:', error);
        }
    };

    // Funksjon for å markere en egendefinert oppgave som fullført
    const handleCompleteCustomTask = async (taskId, taskDescription, dueDate) => {
        const bodyData = {
            task: taskDescription,
            taskType: 'custom',
            dueDate,
            dateCompleted: new Date().toISOString(),
            employee,
            store: butikkid,
        };

        try {
            const completedResponse = await fetch(`${API_BASE_URL}/completedtasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData),
            });

            if (!completedResponse.ok) throw new Error('Feil ved lagring i completedtasks');

            window.dispatchEvent(new Event('gjoremalUtfort')); // «Siste gjøremål» i menylinjen

            const completedData = await completedResponse.json();
            const completedTaskId = completedData._id;

            const response = await fetch(`${API_BASE_URL}/customtasks/${taskId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    completedBy: employee,
                    dateCompleted: new Date().toISOString(),
                }),
            });

            if (response.ok) {
                setCustomTasks((prevTasks) =>
                    prevTasks.map((task) =>
                        task._id === taskId
                            ? {
                                  ...task,
                                  completedBy: employee,
                                  dateCompleted: new Date(),
                                  completed: !!employee,
                              }
                            : task
                    )
                );
                await fetchCompletedTasks();
                setHighlightedTaskId(completedTaskId);
                setTimeout(() => setHighlightedTaskId(null), 5000);

                // Trigg konfetti-animasjon
                confetti({
                    particleCount: 150,
                    spread: 70,
                    origin: { y: 0.6 },
                });
            } else {
                console.error('Feil ved oppdatering av customtasks', response.statusText);
            }
        } catch (error) {
            console.error('Feil ved håndtering av egendefinert oppgave:', error);
        }
    };

    const handleAddEmployeeToCompletedTask = async (taskId, taskDescription, dueDate) => {
        const bodyData = {
            task: taskDescription,
            taskType: 'custom',
            dueDate,
            dateCompleted: new Date().toISOString(),
            employee,
            store: butikkid,
        };

        try {
            console.log('Sender følgende data til completedtasks:', bodyData);

            // Legg til oppgaven i 'completedtasks' collection med ny ansatt
            const completedResponse = await fetch(`${API_BASE_URL}/completedtasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData),
            });

            if (!completedResponse.ok) {
                throw new Error('Feil ved lagring i completedtasks');
            }

            window.dispatchEvent(new Event('gjoremalUtfort')); // «Siste gjøremål» i menylinjen

            console.log('Oppgaven ble lagret i completedtasks');

            const patchData = { extraEmployeeAdded: true };
            console.log('Sender følgende data til customtasks PATCH:', patchData);

            // Oppdater 'customtasks' for å sette `extraEmployeeAdded` til true
            const patchResponse = await fetch(`${API_BASE_URL}/customtasks/${taskId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(patchData),
            });

            if (!patchResponse.ok) {
                throw new Error('Feil ved oppdatering av customtasks');
            }

            console.log('Oppgaven ble oppdatert i customtasks med extraEmployeeAdded');

            // Oppdater lokal tilstand for å skjule "+"-knappen etter første bruk
            setCustomTasks((prevTasks) =>
                prevTasks.map((task) =>
                    task._id === taskId ? { ...task, extraEmployeeAdded: true } : task
                )
            );
        } catch (error) {
            console.error('Feil ved oppdatering av fullført oppgave:', error);
        }
    };

    const apneAvslutt = (taskId, taskType) =>
        setAvslutter({ id: taskId, type: taskType, employee: '', dato: today, feil: '', lagrer: false });

    const handleAvsluttForAnsatt = async (e) => {
        e.preventDefault();
        if (!avslutter.employee || !avslutter.dato) {
            setAvslutter((a) => ({ ...a, feil: 'Velg ansatt og dato.' }));
            return;
        }
        setAvslutter((a) => ({ ...a, feil: '', lagrer: true }));
        try {
            const response = await fetch(`${API_BASE_URL}/completedtasks/for-ansatt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Ansatt': encodeURIComponent(Cookies.get('selectedEmployee') || ''),
                },
                body: JSON.stringify({
                    taskId: avslutter.id,
                    taskType: avslutter.type,
                    employee: avslutter.employee,
                    dato: avslutter.dato,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setAvslutter((a) => ({ ...a, lagrer: false, feil: data.message || 'Kunne ikke avslutte oppgaven.' }));
                return;
            }
            window.dispatchEvent(new Event('gjoremalUtfort')); // «Siste gjøremål» i menylinjen
            // Fast oppgave avsluttet for en tidligere dag: bare historikken endres, dagens står åpen.
            const oppdater = (liste) => liste.map((t) => (t._id === data.oppgave._id ? data.oppgave : t));
            if (avslutter.type === 'daily') setDailyTasks(oppdater);
            else setCustomTasks(oppdater);
            setAvslutter(null);
            await fetchCompletedTasks();
            setHighlightedTaskId(data.completedTask._id);
            setTimeout(() => setHighlightedTaskId(null), 5000);
        } catch (error) {
            setAvslutter((a) => ({ ...a, lagrer: false, feil: 'Kunne ikke kontakte serveren.' }));
        }
    };

    const handleEditTask = (taskId, currentText) => {
        setEditableTaskId(taskId);
        setEditableTaskText(currentText);
    };

    const handleTaskTextChange = (e) => {
        setEditableTaskText(e.target.value);
    };

    const handleSaveTaskText = async (taskId) => {
        const updatedTasks = customTasks.map((task) => {
            if (task._id === taskId) {
                return { ...task, task: editableTaskText };
            }
            return task;
        });
        console.log('Oppdaterte oppgaver:', updatedTasks);
        setCustomTasks(updatedTasks);

        console.log('Oppgave som skal oppdateres:', taskId, editableTaskText);
        // Update backend
        try {
            const response = await fetch(`${API_BASE_URL}/customtasks/${taskId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ task: editableTaskText }),
            });

            if (!response.ok) {
                console.error('Feil ved oppdatering av oppgave:', response.statusText);
            }
        } catch (error) {
            console.error('Feil ved oppdatering av oppgave:', error);
        }

        setEditableTaskId(null);
        setEditableTaskText('');
    };

    // Utseende i det nye designet (vinnsel, 30.10), samme stil som Gjøremål på kontrollpanelet.
    // Handlingene er de samme som før.
    const utfortKnapp = 'inline-flex min-h-[2.25rem] shrink-0 items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1 text-sm font-semibold text-white hover:bg-emerald-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';
    const radApen = 'flex flex-wrap items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-1.5 min-h-[2.75rem]';
    const radUtfort = 'flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 min-h-[2.75rem]';
    const Hake = () => (
        <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white'>
            <Ikon navn='hake' className='w-5 h-5' />
        </span>
    );
    const Boks = () => <span aria-hidden='true' className='h-6 w-6 shrink-0 rounded-lg border-2 border-slate-400 bg-white' />;
    const avsluttKnapp = (taskId, taskType, tekst) =>
        leder && avslutter?.id !== taskId && (
            <button
                type='button'
                onClick={() => apneAvslutt(taskId, taskType)}
                aria-label={`Avslutt «${tekst}» for en ansatt`}
                title='Avslutt for en ansatt som glemte det'
                className='inline-flex min-h-[2.25rem] shrink-0 items-center gap-1.5 rounded-lg border-2 border-slate-300 bg-white px-2.5 py-1 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50'
            >
                <Ikon navn='person' className='w-4 h-4' /> For ansatt
            </button>
        );

    // Skjemaet åpnes under oppgaven (hele bredden i raden).
    const avsluttSkjema = (taskId) =>
        avslutter?.id === taskId && (
            <form onSubmit={handleAvsluttForAnsatt} className='flex w-full flex-wrap items-end gap-2 border-t border-slate-200 pt-2'>
                <div className='min-w-0 flex-1 basis-40'>
                    <label htmlFor={`avslutt-ansatt-${taskId}`} className='mb-1 block text-sm font-semibold text-slate-800'>Utført av</label>
                    <select
                        id={`avslutt-ansatt-${taskId}`}
                        value={avslutter.employee}
                        onChange={(e) => setAvslutter((a) => ({ ...a, employee: e.target.value }))}
                        className={feltKlasse}
                        autoFocus
                    >
                        <option value=''>Velg ansatt</option>
                        {ansatte.map((navn) => <option key={navn} value={navn}>{navn}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor={`avslutt-dato-${taskId}`} className='mb-1 block text-sm font-semibold text-slate-800'>Dato</label>
                    <input
                        id={`avslutt-dato-${taskId}`}
                        type='date'
                        max={today}
                        value={avslutter.dato}
                        onChange={(e) => setAvslutter((a) => ({ ...a, dato: e.target.value }))}
                        className={`${feltKlasse} w-auto`}
                    />
                </div>
                <button type='submit' disabled={avslutter.lagrer} className={knappPrimar}>
                    <Ikon navn='hake' className='w-5 h-5' /> Avslutt
                </button>
                <button type='button' onClick={() => setAvslutter(null)} className={knappSekundar}>Avbryt</button>
                {avslutter.feil && <p role='alert' className='w-full text-sm font-semibold text-red-700'>{avslutter.feil}</p>}
            </form>
        );

    const antallGjort = (liste) => liste.filter((t) => t.completed || (t.dateCompleted && t.completedBy)).length;
    const sisteFullforte = completedTasks
        .filter((task) => task.store === butikkid) // Filtrer på butikkid
        .sort((a, b) => new Date(b.dateCompleted) - new Date(a.dateCompleted)) // Nyeste først
        .slice(0, 10); // De siste 10

    const Seksjonstopp = ({ id, ikon, tittel, liste }) => (
        <div className='mb-2 flex flex-wrap items-center gap-x-2 gap-y-1'>
            <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-700 text-white'>
                <Ikon navn={ikon} className='w-5 h-5' />
            </span>
            <h2 id={id} className='text-lg font-bold text-slate-900'>{tittel}</h2>
            {liste.length > 0 && (
                <span className='ml-auto rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700'>
                    {antallGjort(liste)} av {liste.length} gjort
                </span>
            )}
        </div>
    );

    return (
        <div className='mx-auto max-w-7xl space-y-4 pb-10'>
            <Tilbake />
            <Sidetopp ikon='avkrysning' farge='info' tittel='Gjøremål' undertittel='Hva som må gjøres i dag' />

            <div className='grid gap-4 lg:grid-cols-2'>
                {/* Faste oppgaver */}
                <section aria-labelledby='todo-faste' className='min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-3'>
                    <Seksjonstopp id='todo-faste' ikon='gjenta' tittel='Faste oppgaver' liste={dailyTasks} />
                    {dailyTasks.length === 0 ? (
                        <p className='rounded-xl border border-dashed border-slate-300 px-3 py-2 text-slate-600'>Ingen faste oppgaver.</p>
                    ) : (
                        <ul className='space-y-1.5'>
                            {dailyTasks.map((task) => (
                                <li key={task._id} className={task.completed ? radUtfort : radApen}>
                                    {task.completed ? <Hake /> : <Boks />}
                                    <span className='min-w-0 flex-1 basis-40'>
                                        <span className={`block [overflow-wrap:anywhere] ${task.completed ? 'text-slate-600 line-through decoration-slate-400' : 'font-medium text-slate-900'}`}>
                                            {task.task}
                                        </span>
                                        {task.completed && (
                                            <span className='block text-sm text-emerald-800'>Utført av {task.completedBy}</span>
                                        )}
                                    </span>
                                    {!task.completed ? (
                                        <button type='button' onClick={() => handleCompleteDailyTask(task._id, task.task)} className={utfortKnapp}>
                                            <Ikon navn='hake' className='w-4 h-4' /> Merk som utført
                                        </button>
                                    ) : (
                                        <span className='text-sm font-semibold text-emerald-800'>Utført</span>
                                    )}
                                    {/* Fast oppgave: også når den er krysset av i dag, for en tidligere dag. */}
                                    {avsluttKnapp(task._id, 'daily', task.task)}
                                    {avsluttSkjema(task._id)}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Dagens oppgaver */}
                <section aria-labelledby='todo-dagens' className='min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-3'>
                    <Seksjonstopp id='todo-dagens' ikon='kalender' tittel='Dagens oppgaver' liste={customTasks} />
                    {customTasks.length === 0 ? (
                        <p className='rounded-xl border border-dashed border-slate-300 px-3 py-2 text-slate-600'>Ingen oppgaver i dag.</p>
                    ) : (
                        <ul className='space-y-1.5'>
                            {customTasks.map((task) => {
                                const utfort = task.dateCompleted && task.completedBy;
                                return (
                                    <li key={task._id} className={utfort ? radUtfort : radApen}>
                                        {utfort ? <Hake /> : <Boks />}
                                        <span className='min-w-0 flex-1 basis-40'>
                                            {editableTaskId === task._id && !task.dateCompleted ? (
                                                <input
                                                    type='text'
                                                    aria-label='Endre oppgaven'
                                                    value={editableTaskText}
                                                    onChange={handleTaskTextChange}
                                                    onBlur={() => handleSaveTaskText(task._id)}
                                                    autoFocus
                                                    className={feltKlasse}
                                                />
                                            ) : !task.dateCompleted ? (
                                                // Trykk på teksten for å endre den (som før).
                                                <button
                                                    type='button'
                                                    onClick={() => handleEditTask(task._id, task.task)}
                                                    title='Trykk for å endre'
                                                    className='block w-full rounded text-left font-medium text-slate-900 [overflow-wrap:anywhere] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600'
                                                >
                                                    {task.task}
                                                </button>
                                            ) : (
                                                <span className='block text-slate-600 line-through decoration-slate-400 [overflow-wrap:anywhere]'>{task.task}</span>
                                            )}
                                            {utfort && (
                                                <span className='block text-sm text-emerald-800'>
                                                    Utført av {task.completedBy}, {new Date(task.dateCompleted).toLocaleDateString()}
                                                </span>
                                            )}
                                        </span>
                                        {!utfort ? (
                                            <button
                                                type='button'
                                                onClick={() => handleCompleteCustomTask(task._id, task.task, task.dueDate)}
                                                className={utfortKnapp}
                                            >
                                                <Ikon navn='hake' className='w-4 h-4' /> Merk som utført
                                            </button>
                                        ) : (
                                            <span className='text-sm font-semibold text-emerald-800'>Utført</span>
                                        )}
                                        {utfort && !task.extraEmployeeAdded && (
                                            <button
                                                type='button'
                                                onClick={() => handleAddEmployeeToCompletedTask(task._id, task.task, task.dueDate)}
                                                aria-label='Legg til meg også som utfører'
                                                title='Legg til meg også som utfører'
                                                className='inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-2 border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50'
                                            >
                                                <Ikon navn='pluss' className='w-4 h-4' />
                                            </button>
                                        )}
                                        {!utfort && avsluttKnapp(task._id, 'custom', task.task)}
                                        {avsluttSkjema(task._id)}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* Legg til ny oppgave */}
                    <div className='mt-3 flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3'>
                        <div className='min-w-0 flex-1 basis-56'>
                            <label htmlFor='todo-ny' className='mb-1 block font-semibold text-slate-800'>Ny oppgave</label>
                            <input
                                id='todo-ny'
                                type='text'
                                placeholder='Hva skal gjøres?'
                                value={newCustomTask}
                                onChange={(e) => setNewCustomTask(e.target.value)}
                                className={feltKlasse}
                            />
                        </div>
                        <div>
                            <label htmlFor='todo-dato' className='mb-1 block font-semibold text-slate-800'>Dato</label>
                            <input
                                id='todo-dato'
                                type='date'
                                value={customTaskDate}
                                onChange={(e) => setCustomTaskDate(e.target.value)}
                                className={`${feltKlasse} w-auto`}
                            />
                        </div>
                        <button type='button' onClick={handleAddCustomTask} className={knappPrimar}>
                            <Ikon navn='pluss' className='w-5 h-5' /> Legg til oppgave
                        </button>
                    </div>
                </section>
            </div>

            {/* Siste 10 fullførte oppgaver */}
            <section aria-labelledby='todo-siste' className='space-y-2'>
                <div className='flex flex-wrap items-center gap-2'>
                    <h2 id='todo-siste' className='text-lg font-bold text-slate-900'>Siste 10 fullførte oppgaver</h2>
                </div>
                {sisteFullforte.length === 0 ? (
                    <p className='rounded-xl border border-dashed border-slate-300 px-3 py-2 text-slate-600'>Ingen fullførte oppgaver ennå.</p>
                ) : (
                    <div className='overflow-x-auto rounded-2xl border border-slate-200 bg-white'>
                        <table className='min-w-full text-left'>
                            <thead className='bg-slate-100 text-sm font-semibold text-slate-700'>
                                <tr>
                                    <th scope='col' className='px-3 py-1.5'>Oppgave</th>
                                    <th scope='col' className='px-3 py-1.5'>Utført av</th>
                                    <th scope='col' className='hidden px-3 py-1.5 md:table-cell'>Dato</th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-slate-200 text-slate-800'>
                                {sisteFullforte.map((task) => (
                                    <tr
                                        key={task._id}
                                        className={`hover:bg-slate-50 ${task._id === highlightedTaskId ? 'outline outline-2 -outline-offset-2 outline-emerald-500' : ''}`}
                                    >
                                        <td className='px-3 py-1.5 [overflow-wrap:anywhere]'>{task.task}</td>
                                        <td className='px-3 py-1.5'>
                                            {task.employee}
                                            {task.registrertAv && (
                                                <span className='block text-sm text-slate-600'>Registrert av {task.registrertAv}</span>
                                            )}
                                        </td>
                                        <td className='hidden whitespace-nowrap px-3 py-1.5 text-sm md:table-cell'>
                                            {new Date(task.dateCompleted).toLocaleDateString()}{' '}
                                            {new Date(task.dateCompleted).toLocaleTimeString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

export default TodoList;
